import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CreatePositionActions } from './index';
import { catchError, debounceTime, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { ApiService } from '../../services/api.service';
import { Store } from '@ngrx/store';
import { CreatePositionState, selectAdditionalInfo, selectHardSkills, selectJobPositionLevel, selectJobTitles, selectRequireValidation, selectSoftSkills, selectStepsState, selectVacancyId } from './create-position.store';
import { CREATE_POSITION_STEPS } from './create-position.proxy';
import { CreatePositionAdapter, CreatePositionSkillsAdapter } from './create-position.adapter';
import { SetAdditionalJobPositions, StepValid } from './create-position.actions';
import { VACANCY_STATUS } from '../dashboard/dashboard.proxy';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardActions } from '../dashboard';
import { UserProxy } from '../user/user.proxy';

@Injectable()
export class CreatePositionEffects {

  checkServerSteps$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.CheckServerSteps),
    withLatestFrom(this.store.select(selectVacancyId)),
    tap(([ action, vacancyId ]) => {
      if (vacancyId) {
        this.apiService.getCreatePositionSteps(vacancyId).subscribe(stepRes => {
          if (stepRes && !stepRes.error && stepRes.stepItems) {
            const normalizedStepState = this.normalizeStepState(stepRes.stepItems, stepRes.currentStep);
            this.store.dispatch(CreatePositionActions.SetSteps({ 
              steps: normalizedStepState.steps, currentStep: normalizedStepState.currentStep 
            }));
          } else {
            this.store.dispatch(CreatePositionActions.SetSteps({ 
              steps: this.setSteps(), currentStep: 0 
            }));
          }
          this.store.dispatch(CreatePositionActions.GetVacancy({ vacancyId }));
        });
      } else {
        this.store.dispatch(CreatePositionActions.SetSteps({ steps: this.setSteps(), currentStep: 0 }))
      }
    })
  ), { dispatch: false });

  initSteps$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.InitSteps),
    tap(() => {
      return this.store.dispatch(CreatePositionActions.SetSteps({ steps: this.setSteps(), currentStep: 0 }));
    })
  ), { dispatch: false });

  setCurrentSteps$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetCurrentStep),
    withLatestFrom(this.store.select(selectStepsState)),
    tap(([ action, steps ]) => {
      return this.store.dispatch(CreatePositionActions.SetSteps({ steps, currentStep: action.currentStep }));
    })
  ), { dispatch: false });

  getVacancy$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.GetVacancy),
    switchMap(action => {
      return this.apiService.getVacancyById(action.vacancyId).pipe(
        withLatestFrom(this.store.select(selectHardSkills)),
        map(([vacancyRes, currentSkills]) => {
          if (vacancyRes) {
            const hardSkills = action.useServerHardSkills
              ? vacancyRes.hardSkills
              : CreatePositionSkillsAdapter.toViewMerge(currentSkills, vacancyRes.hardSkills);
            return CreatePositionActions.GetVacancySuccess({ 
              vacancy: CreatePositionAdapter.toStore({ 
                ...vacancyRes, 
                hardSkills: hardSkills ? hardSkills : vacancyRes.hardSkills 
              })
            });
          } else {
            return CreatePositionActions.GetVacancyFailure();
          }
        }),
        catchError((err) => of())
      )
    })
  ));

  getJobTitles$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.GetJobTitles),
    withLatestFrom(this.store.select(selectJobTitles)),
    switchMap(([ action, jobTitles ]) => {
      if (!jobTitles) {
        return this.apiService.getJobTitles().pipe(
          map(jobTitles => CreatePositionActions.GetJobTitlesSuccess({ jobTitles })),
          catchError((err) => of(CreatePositionActions.GetJobTitlesFailure({ err })))
        )
      }
      return of();
    })
  ));

  setSteps$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetSteps),
    withLatestFrom(this.store.select(selectVacancyId)),
    tap(([ action, vacancyId ]) => {
      if (vacancyId) {
        this.apiService.setCreatePositionSteps(vacancyId, action.steps, action.currentStep).subscribe();
      }
    })
  ), { dispatch: false });

  setJobPosition$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetJobPosition),
    withLatestFrom(this.store.select(selectVacancyId)),
    tap(([ action, vacancyId ]) => {
      const dispatchValid = () => {
        this.store.dispatch(StepValid({ 
          key: CREATE_POSITION_STEPS.jobPosition,
          data: action.jobPosition, 
          wasChanged: action.wasChanged 
        }));
        this.store.dispatch(SetAdditionalJobPositions({ jobPositions: action.jobPositions }));
      };
      const dispatchFailure = (error) => {
        this.store.dispatch(CreatePositionActions.SetJobPositionFailure({ error: this.normalizeBackendError(error) }));
      };
      if (vacancyId && action.wasChanged) {
        this.apiService.setJobPosition(
          vacancyId,
          action.jobPosition,
          action.jobPositions,
          action.jobDescription,
          action.selectedJobDescriptionFile
        ).subscribe({
          next: async (res : any) => {
            if (res && res.error) {
              dispatchFailure(res.error);
              return;
            }
            this.store.dispatch(CreatePositionActions.GetVacancy({ vacancyId, useServerHardSkills: true }));
            await firstValueFrom(this.actions$.pipe(ofType(CreatePositionActions.GetVacancySuccess)));
            dispatchValid();
          },
          error: dispatchFailure
        });
      } else if (!vacancyId) {
        this.apiService.createVacancy(
          action.jobPosition,
          action.jobPositions,
          action.jobDescription,
          action.selectedJobDescriptionFile
        ).subscribe(async (res: any) => {
          if (res?.error) {
            dispatchFailure(res.error);
            return;
          }

          const createdVacancyId = res.data;
          if (!createdVacancyId) {
            dispatchFailure('Failed to create vacancy. Please choose valid job title and expertise.');
            return;
          }
          this.store.dispatch(CreatePositionActions.SetVacancyId({ vacancyId: createdVacancyId }));
          this.store.dispatch(CreatePositionActions.GetVacancy({ vacancyId: createdVacancyId, useServerHardSkills: true }));
          await firstValueFrom(this.actions$.pipe(ofType(CreatePositionActions.GetVacancySuccess)));
          await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { vacancyId: createdVacancyId, jobTitle: null, jobTitles: null },
            queryParamsHandling: 'merge',
            skipLocationChange: true
          });
          dispatchValid();
        }, dispatchFailure);
      } else {
        dispatchValid();
      }
    })
  ), { dispatch: false });

  stepValid$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.StepValid),
    withLatestFrom(this.store.select(selectVacancyId), this.store.select(selectJobPositionLevel),this.store.select(selectRequireValidation)),
    tap(([ action, vacancyId, jobPositionLevel,isValidationRequired ]) => {
      if (action.key === CREATE_POSITION_STEPS.additional) {
        const payload = CreatePositionAdapter.toServer({ ...action.data, jobPositionLevel, isValidationRequired });
        this.apiService.setVacancyInfo(vacancyId, payload).subscribe();
      }
    })
  ), { dispatch: false });
  
  getSkillsList$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.GetSkillsList),
    withLatestFrom(this.store.select(selectVacancyId)),
    switchMap(([ action, vacancyId ]) => {
      return this.apiService.getVacancySkillTree(vacancyId).pipe(
        withLatestFrom(this.store.select(selectHardSkills)),
        map(([ skillTree, currentSkills ]) => {
          const skillsList = CreatePositionSkillsAdapter.toViewMerge(skillTree.branches, currentSkills);
          return CreatePositionActions.SetHardSkills({ skillsList, noServer: true });
        }),
        catchError((err) => of(CreatePositionActions.SetHardSkills({ skillsList: [] })))
      );
    })
  ));

  setHardSkills$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetHardSkills),
    withLatestFrom(this.store.select(selectVacancyId)),
    debounceTime(1000),
    tap(([ action, vacancyId ]) => {
      const skills = CreatePositionSkillsAdapter.toServer(action.skillsList);
      if (!action.noServer && skills) {
        this.apiService.setVacancyHardSkills(vacancyId, skills).subscribe();
      }
    })
  ), { dispatch: false });

  getSoftSkills$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.GetSoftSkills),
    withLatestFrom(this.store.select(selectSoftSkills), this.store.select(selectVacancyId)),
    switchMap(([ action, skills, vacancyId ]) => {
      if (!skills) {
        return this.apiService.getVacancySoftSkills(vacancyId).pipe(
          map(skillsList => CreatePositionActions.GetSoftSkillsSuccess({ skillsList })),
          catchError((err) => of(CreatePositionActions.GetSoftSkillsFailure({ err })))
        )
      }
      return of();
    })
  ));

  setSoftSkills$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetSoftSkills),
    withLatestFrom(this.store.select(selectVacancyId)),
    debounceTime(3000),
    tap(([ action, vacancyId ]) => {
      this.apiService.setVacancySoftSkills(vacancyId, action.skillsList).subscribe();
    })
  ), { dispatch: false });

  saveVacancy$ = createEffect(() => this.actions$.pipe(
  ofType(CreatePositionActions.SaveVacancy),
  withLatestFrom(this.store.select(selectVacancyId)),
  tap(([ action, vacancyId ]) => {
    if (vacancyId) {
      this.store.dispatch(DashboardActions.ClearHmVacancies());
      const status = action.publish ? VACANCY_STATUS.published : VACANCY_STATUS.draft;

     const callSetVacancyStatus = (boards: string[]) => {
        this.apiService.setVacancyStatus(vacancyId, status, boards).subscribe({
          next: () => {
            if(action.publish){
              this.apiService.showVacancyById(vacancyId).subscribe({
                next: (data) => {
                  this.store.dispatch(CreatePositionActions.PublishCompleted({
                    vacancyId: vacancyId,
                    status: status, 
                    approvedEmailAutomation: !!data?.approvedEmailAutomation
                  }));
                },
                error: () => {
                  this.store.dispatch(CreatePositionActions.PublishCompleted({
                    vacancyId: vacancyId,
                    status: status, 
                    approvedEmailAutomation: false
                  }));
                }
              });
            } else {
              this.store.dispatch(CreatePositionActions.SaveDraftCompleted());
            }
             if (action.redirect) {
               localStorage.setItem('selected_vacancy_tab', status);
               if (action.publish) {
                 this.router.navigate(['dashboard/vacancies'], { queryParams: { vacancyId } });
               } else {
                 this.router.navigate(['dashboard/vacancies/list']);
               }
             }
          },
          error: (err) => {
            this.store.dispatch(CreatePositionActions.SetVacancyStatusFailure({ error: err }));
          }
        });
      };

      if (action.publish) {
        this.userProxy.getClaimValue('Vacancy.AvailableJobBoards').subscribe(value => {
          let jobBoards: string[] = [];
          if (value) {
            jobBoards = value.split(',').map((b: string) => b.trim());
          }
          callSetVacancyStatus(jobBoards);
        });
      } else {
        callSetVacancyStatus([]);
      }
    } else {
      this.router.navigate(['dashboard/vacancies/list']);
    }
  })
), { dispatch: false });

  setSeniority$ = createEffect(() => this.actions$.pipe(
    ofType(CreatePositionActions.SetSeniority),
    withLatestFrom(this.store.select(selectVacancyId), this.store.select(selectAdditionalInfo),),
    switchMap(([ action, vacancyId, additionalInfo ]) => {
      if (action.level) {
        const payload = CreatePositionAdapter.toServer({ ...additionalInfo, jobPositionLevel: action.level });
        return this.apiService.setVacancyInfo(vacancyId, payload).pipe(
          map(() => CreatePositionActions.GetVacancy({ vacancyId })),
          catchError((err) => of())
        );
      } else {
        return this.apiService.resetHardSkillsPreset(vacancyId).pipe(
          map(() => CreatePositionActions.GetVacancy({ vacancyId })),
          catchError((err) => of())
        );
      }
    })
  ));

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private actions$: Actions,
    private apiService: ApiService,
    private store: Store<CreatePositionState>,
    private userProxy: UserProxy,
  ) { }

  private setSteps() {
    return this.filterHiddenSteps([
      { key: 'jobPosition', path: 'job-position', label: 'Job position', isComplited: false },
      { key: 'hardSkills', path: 'hard-skills', label: 'Hard skills', isComplited: false },
      { key: 'softSkills', path: 'soft-skills', label: 'Soft skills', isComplited: false },
      { key: 'additionalInfo', path: 'additional', label: 'Additional Info', isComplited: false },
      { key: 'uploadCvs', path: 'upload-cvs', label: 'Upload CVs', isComplited: false },
      { key: 'checkout', path: 'checkout', label: 'Complete job posting', isComplited: false }
    ]);
  }

  private filterHiddenSteps(steps: any[]) {
    return steps.filter(step => step.key !== CREATE_POSITION_STEPS.soft);
  }

  private normalizeStepState(steps: any[], currentStep: number) {
    const safeCurrentStep = typeof currentStep === 'number' && currentStep >= 0 ? currentStep : 0;
    const visibleSteps = this.filterHiddenSteps(steps);
    const currentStepItem = steps[safeCurrentStep];
    let visibleCurrentStep = visibleSteps.findIndex(step => step.key === currentStepItem?.key && step.path === currentStepItem?.path);

    if (visibleCurrentStep === -1) {
      const nextVisibleStep = steps.slice(safeCurrentStep + 1).find(step => step.key !== CREATE_POSITION_STEPS.soft);
      const previousVisibleStep = [...steps.slice(0, safeCurrentStep)].reverse().find(step => step.key !== CREATE_POSITION_STEPS.soft);
      const fallbackStep = nextVisibleStep || previousVisibleStep || visibleSteps[0];
      visibleCurrentStep = visibleSteps.findIndex(step => step.key === fallbackStep?.key && step.path === fallbackStep?.path);
    }

    return {
      steps: visibleSteps,
      currentStep: visibleCurrentStep === -1 ? 0 : visibleCurrentStep
    };
  }

  private normalizeBackendError(error: any) {
    return error?.error || error;
  }
}
